import { Fragment, type ReactNode } from 'react'
import {
  type AccuracyField,
  type AdminAirline,
  FIELD_WORD,
  type FieldWord,
  type ManualCheck,
  type RawState,
  day,
  moment,
  percent,
} from '../../lib/admin-airlines'
import { fmtNum } from '../../lib/format'

type Tone = 'red' | 'yellow' | 'green' | 'quiet'

const PILL: Record<Tone, string> = {
  red: 'bg-down/10 text-down ring-down/25',
  yellow: 'bg-amber-500/10 text-amber-700 ring-amber-500/30',
  green: 'bg-up/10 text-up ring-up/25',
  quiet: 'bg-surface-2 text-muted ring-border',
}

const DOT: Record<Tone, string> = {
  red: 'bg-down',
  yellow: 'bg-amber-500',
  green: 'bg-up',
  quiet: 'bg-faint',
}

const STATE_WORD: Record<AdminAirline['state'], string> = {
  red: 'Down',
  yellow: 'Degraded',
  green: 'Up',
  quiet: 'Quiet',
}

const VERDICT_WORD: Record<string, string> = {
  match: 'match',
  behind: 'behind',
  mismatch: 'mismatch',
}

const COLUMNS = 9
const TH = 'whitespace-nowrap px-3 py-2.5 font-medium'
const TD = 'px-3 py-3 align-top'

interface Props {
  airlines: AdminAirline[]
  checks: ManualCheck[]
  open: string | null
  raw: RawState
  shown: { path: string; text: string } | null
  onToggle: (prefix: string) => void
  onOpenRaw: (path: string) => void
}

/** The owner's table of lines in the order given; a click opens the line's waybills, accuracy check and raw answers. */
export function AirlinesBoard({ airlines, checks, open, raw, shown, onToggle, onOpenRaw }: Props) {
  const checkOf = new Map(checks.map((c) => [c.prefix, c]))
  const most = Math.max(1, ...airlines.map((a) => a.refusals.asked_7d))
  return (
    <table className="w-full min-w-[920px] text-left text-sm">
      <thead className="border-b border-border bg-surface-1 text-[11px] uppercase tracking-wider text-muted">
        <tr>
          <th className={`${TH} w-10 text-right`}>#</th>
          <th className={TH}>Airline</th>
          <th className={TH}>Status</th>
          <th className={`${TH} text-right`}>Requests 7d</th>
          <th className={`${TH} text-right`}>Requests 24h</th>
          <th className={`${TH} text-right`}>Refused 24h</th>
          <th className={`${TH} text-right`}>Refused 7d</th>
          <th className={TH}>Accuracy</th>
          <th className={TH}>Manual check</th>
        </tr>
      </thead>
      <tbody>
        {airlines.map((a, i) => (
          <Fragment key={a.prefix}>
            <LineRow
              rank={i + 1}
              airline={a}
              most={most}
              open={open === a.prefix}
              check={checkOf.get(a.prefix)}
              onToggle={onToggle}
            />
            {open === a.prefix && (
              <tr>
                <td colSpan={COLUMNS} className="border-b border-border bg-surface-1 px-4 py-5">
                  <LineDetail
                    airline={a}
                    check={checkOf.get(a.prefix)}
                    raw={raw}
                    shown={shown}
                    onOpenRaw={onOpenRaw}
                  />
                </td>
              </tr>
            )}
          </Fragment>
        ))}
      </tbody>
    </table>
  )
}

function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${PILL[tone]}`}
    >
      {children}
    </span>
  )
}

function LineRow(p: {
  rank: number
  airline: AdminAirline
  most: number
  open: boolean
  check: ManualCheck | undefined
  onToggle: (prefix: string) => void
}) {
  const a = p.airline
  const r = a.refusals
  return (
    <tr
      id={`line-${a.prefix}`}
      className={`scroll-mt-24 border-b border-border transition-colors hover:bg-surface-1 ${p.open ? 'bg-surface-1' : ''}`}
    >
      <td className={`${TD} mono text-right text-xs text-faint tabular-nums`}>{p.rank}</td>
      <td className={TD}>
        <button
          type="button"
          aria-expanded={p.open}
          className="group flex items-start gap-2 text-left"
          onClick={() => p.onToggle(a.prefix)}
        >
          <span
            className={`mt-0.5 text-faint transition-transform ${p.open ? 'rotate-90' : ''}`}
            aria-hidden
          >
            ›
          </span>
          <span>
            <span className="block font-medium text-ink group-hover:text-signal">{a.name}</span>
            <span className="mono block text-xs text-faint">
              {a.prefix} · {a.iata}
            </span>
          </span>
        </button>
      </td>
      <td className={TD} data-state={a.state}>
        <Pill tone={a.state}>
          <span className={`h-1.5 w-1.5 rounded-full ${DOT[a.state]}`} />
          {STATE_WORD[a.state]}
        </Pill>
        <StateNotes airline={a} />
      </td>
      <td className={`${TD} text-right`} data-requests-7d={r.asked_7d}>
        <span className="mono text-base font-semibold text-ink tabular-nums">
          {fmtNum(r.asked_7d, 0)}
        </span>
        <span className="mt-1.5 ml-auto block h-1 w-16 overflow-hidden rounded-full bg-surface-2">
          <span
            className="block h-full rounded-full bg-signal"
            style={{ width: `${(r.asked_7d / p.most) * 100}%` }}
          />
        </span>
      </td>
      <td className={`${TD} mono text-right text-muted tabular-nums`}>{fmtNum(r.asked_24h, 0)}</td>
      <td className={`${TD} text-right`} data-refusal={r.level ?? 'none'}>
        {r.share_24h === null ? (
          <span className="text-faint">—</span>
        ) : (
          <Pill tone={r.level ?? 'quiet'}>
            <span className="mono tabular-nums">{percent(r.share_24h)}</span>
          </Pill>
        )}
        <span className="mono mt-1 block text-xs text-faint tabular-nums">
          {fmtNum(r.refused_24h, 0)} of {fmtNum(r.asked_24h, 0)}
        </span>
      </td>
      <td className={`${TD} text-right`}>
        <span className="mono text-ink tabular-nums">{percent(r.share_7d)}</span>
        <span className="mono mt-1 block text-xs text-faint tabular-nums">
          {fmtNum(r.refused_7d, 0)} of {fmtNum(r.asked_7d, 0)}
        </span>
      </td>
      <td className={TD}>
        <AccuracySummary accuracy={a.accuracy} />
      </td>
      <td className={`${TD} whitespace-nowrap`}>
        {p.check ? day(p.check.checked_at) : <span className="text-faint">Never</span>}
      </td>
    </tr>
  )
}

function StateNotes({ airline: a }: { airline: AdminAirline }) {
  const failingDays = a.probe?.failed_days ?? 0
  if (!a.last_error && failingDays === 0) return null
  return (
    <span className="mt-1 block text-xs text-faint">
      {a.last_error && <span className="mono">{a.last_error}</span>}
      {a.last_error && failingDays > 0 && ' · '}
      {failingDays > 0 && `canary failing ${failingDays}d`}
    </span>
  )
}

function AccuracySummary({ accuracy }: { accuracy: AdminAirline['accuracy'] }) {
  if (!accuracy) return <span className="text-faint">Not checked</span>
  return (
    <span className="whitespace-nowrap text-xs">
      <span className="text-up">{accuracy.match} match</span>
      <span className="text-faint"> · </span>
      <span className={accuracy.behind > 0 ? 'text-amber-700' : 'text-muted'}>
        {accuracy.behind} behind
      </span>
      <span className="text-faint"> · </span>
      <span className={accuracy.mismatch > 0 ? 'font-medium text-down' : 'text-muted'}>
        {accuracy.mismatch} mismatch
      </span>
    </span>
  )
}

function Section({
  title,
  aside,
  children,
}: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-surface-0 p-4">
      <h3 className="mb-2 flex flex-wrap items-baseline gap-x-2 text-[11px] font-medium uppercase tracking-wider text-muted">
        {title}
        {aside && <span className="mono normal-case tracking-normal text-faint">{aside}</span>}
      </h3>
      <div className="text-sm text-ink">{children}</div>
    </section>
  )
}

function LineDetail({
  airline: a,
  check,
  raw,
  shown,
  onOpenRaw,
}: {
  airline: AdminAirline
  check: ManualCheck | undefined
  raw: RawState
  shown: { path: string; text: string } | null
  onOpenRaw: (path: string) => void
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Section title="Customer waybills, last 24h">
        {a.asked.length === 0 ? (
          <p className="text-faint">No requests.</p>
        ) : (
          <ul className="divide-y divide-border">
            {a.asked.map((w) => (
              <li
                key={w.awb}
                className="flex flex-wrap items-baseline justify-between gap-2 py-1.5"
              >
                <span>
                  <span className="mono">{w.awb}</span>{' '}
                  <span className="text-muted">{w.org ?? 'unknown org'}</span>
                </span>
                <span className="mono text-xs text-muted tabular-nums">
                  {fmtNum(w.calls_24h, 0)} requests · {fmtNum(w.failed_24h, 0)} refused
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Live accuracy check"
        aside={a.accuracy ? moment(a.accuracy.probed_at) : undefined}
      >
        {!a.accuracy ? (
          <p className="text-faint">Not in the top 20, or not checked yet.</p>
        ) : a.accuracy.waybills.length === 0 ? (
          <p className="text-faint">No waybills in this check.</p>
        ) : (
          <ul className="divide-y divide-border">
            {a.accuracy.waybills.map((w) => (
              <li key={w.awb} className="py-1.5">
                <span className="mono">{w.awb}</span>{' '}
                <span className="text-muted">{w.org ?? 'unknown org'}</span>:{' '}
                {w.verdict ? VERDICT_WORD[w.verdict] : `no answer${w.code ? ` (${w.code})` : ''}`}
                {w.behind_h !== null && ` by ${w.behind_h}h`}
                {w.fields && <DifferingFields fields={w.fields} />}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Manual check">
        {check ? (
          <p>
            {moment(check.checked_at)}: {check.verdict}
            {check.awb && <span className="mono"> {check.awb}</span>}
            {check.note && <span>. {check.note}</span>}{' '}
            <span className="text-faint">({check.checked_by})</span>
          </p>
        ) : (
          <p className="text-faint">Never checked by hand.</p>
        )}
      </Section>

      <Section title="Raw answers, last 14 days">
        <RawList raw={raw} onOpenRaw={onOpenRaw} />
      </Section>

      {shown && (
        <pre className="mono max-h-96 overflow-auto rounded-xl border border-border bg-surface-2 p-4 text-xs md:col-span-2">
          {shown.text}
        </pre>
      )}
    </div>
  )
}

function DifferingFields({ fields }: { fields: Record<AccuracyField, FieldWord> }) {
  const off = (Object.keys(FIELD_WORD) as AccuracyField[]).filter(
    (f) => fields[f] !== 'same' && fields[f] !== 'neither',
  )
  if (off.length === 0) return null
  return (
    <span className="text-down">
      {' '}
      ({off.map((f) => `${FIELD_WORD[f]}: ${fields[f].replace(/_/g, ' ')}`).join(', ')})
    </span>
  )
}

function RawList({ raw, onOpenRaw }: { raw: RawState; onOpenRaw: (path: string) => void }) {
  if (raw.status === 'idle' || raw.status === 'loading')
    return <p className="text-faint">Loading…</p>
  if (raw.status === 'failed') return <p className="text-down">{raw.message}</p>
  const gaps = [
    raw.failed_days.length > 0 && `Storage did not return: ${raw.failed_days.join(', ')}.`,
    raw.truncated_days.length > 0 && `Partly shown: ${raw.truncated_days.join(', ')}.`,
    raw.total > raw.items.length && `Showing the newest ${raw.items.length} of ${raw.total}.`,
  ].filter(Boolean)
  const note = gaps.length > 0 && <p className="mb-2 text-xs text-down">{gaps.join(' ')}</p>
  if (raw.items.length === 0) return note || <p className="text-faint">No stored answers.</p>
  return (
    <>
      {note}
      <ul className="max-h-64 overflow-auto">
        {raw.items.map((r) => (
          <li key={r.path}>
            <button
              type="button"
              className="py-0.5 text-left text-signal hover:underline"
              data-raw={r.path}
              onClick={() => onOpenRaw(r.path)}
            >
              <span className="mono text-xs">{moment(r.at)}</span>{' '}
              <span className="mono">{r.awb}</span>{' '}
              <span className="text-xs text-muted">{r.source}</span>
            </button>
          </li>
        ))}
      </ul>
    </>
  )
}
